import EditIcon from '@mui/icons-material/Edit';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {Box, CircularProgress, Collapse, Divider, List, ListItem, ListItemText, Paper, Typography} from '@mui/material';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {i18n} from 'src/i18n';
import GestorDocumentalService from 'src/modules/gestorDocumental/gestorDocumentalService';
import layoutActions from 'src/modules/layout/layoutActions';
import DrawerButton from 'src/view/shared/view/DrawerButton';
import GestorDocumentalFormPage from '../form/GestorDocumentalFormPage';
import GestorDocumentalViewPage from './GestorDocumentalViewPage';

interface DocumentosRelacionadosViewProps {
  documentoId: string;
}

const DocumentosRelacionadosView: React.FC<DocumentosRelacionadosViewProps> = ({documentoId}) => {
  const [loadingPrincipal, setLoadingPrincipal] = useState(false);
  const [loadingDerivados, setLoadingDerivados] = useState(false);
  const [loadingHijos, setLoadingHijos] = useState(false);
  const [documentoPrincipal, setDocumentoPrincipal] = useState<any>(null);
  const [documentosDerivados, setDocumentosDerivados] = useState<any[]>([]);
  const [documentosHijos, setDocumentosHijos] = useState<any[]>([]);
  const [openDerivados, setOpenDerivados] = useState(false);
  const [openHijos, setOpenHijos] = useState(false);
  const [derivadosLoaded, setDerivadosLoaded] = useState(false);
  const [hijosLoaded, setHijosLoaded] = useState(false);

  const dispatch = useDispatch();

  const cargarDocumentoPrincipal = async () => {
    if (!documentoId) return;

    try {
      setLoadingPrincipal(true);
      const documento = await GestorDocumentalService.find(documentoId);

      if (documento.documentoPrincipal) {
        const docPrincipal = await GestorDocumentalService.find(documento.documentoPrincipal.id);
        setDocumentoPrincipal(docPrincipal);
      }
    } catch (error) {
      console.error('Error al cargar documento principal:', error);
    } finally {
      setLoadingPrincipal(false);
    }
  };

  const cargarDocumentosRelacionados = async () => {
    if (!documentoId) return;

    try {
      setLoadingDerivados(true);
      setLoadingHijos(true);

      // Esta busqueda obtiene documentos donde este es el documentoPrincipal
      // Estos son documentos para los que este documento es el "padre"
      const resultado = await GestorDocumentalService.list({documentoPrincipal: documentoId}, 'createdAt', 100, 0);

      // Los mismos documentos aparecerán en ambas secciones por ahora
      // En el futuro, si hay diferencia entre "derivados" e "hijos", podemos
      // modificar el filtro según sea necesario
      setDocumentosDerivados(resultado.rows || []);
      setDocumentosHijos(resultado.rows || []);

      setDerivadosLoaded(true);
      setHijosLoaded(true);
    } catch (error) {
      console.error('Error al cargar documentos relacionados:', error);
    } finally {
      setLoadingDerivados(false);
      setLoadingHijos(false);
    }
  };

  useEffect(() => {
    cargarDocumentoPrincipal();
    cargarDocumentosRelacionados();
  }, [documentoId]);

  const handleClickDerivados = () => {
    if (!derivadosLoaded) {
      cargarDocumentosRelacionados();
    }
    setOpenDerivados(!openDerivados);
  };

  const handleClickHijos = () => {
    if (!hijosLoaded) {
      cargarDocumentosRelacionados();
    }
    setOpenHijos(!openHijos);
  };

  const verDocumento = (id: string) => {
    dispatch(
      layoutActions.doVisibleDrawer({
        drawerId: 'DrawerGestorDocumentalViewPage',
        drawerVisible: true,
        drawerChanged: moment().unix(),
        recordId: id,
        drawerUpdateComponent: <GestorDocumentalViewPage id={id} drawer={true} />,
      }),
    );
  };

  // Si no hay documento principal, derivados, ni hijos y no estamos cargándolos, no mostramos nada
  if (!loadingPrincipal && !documentoPrincipal && !loadingDerivados && !loadingHijos && !derivadosLoaded && !hijosLoaded) {
    return (
      <Paper sx={{p: 2, mb: 2}}>
        <Typography variant="subtitle1">{i18n('entities.gestorDocumental.view.noDocumentosRelacionados')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{p: 2, mb: 2}}>
      <Typography variant="h6" gutterBottom>
        {i18n('entities.gestorDocumental.fields.documentosRelacionados')}
      </Typography>

      <List component="nav" aria-label="documentos relacionados">
        {/* Documento Principal */}
        {/* {(loadingPrincipal || documentoPrincipal) && (
          <>
            <ListItem>
              <ListItemText
                primary={i18n('entities.gestorDocumental.fields.documentoPrincipal')}
                secondary={loadingPrincipal ? i18n('common.loading') : null}
              />
            </ListItem>

            {loadingPrincipal ? (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                <CircularProgress size={24} />
              </Box>
            ) : documentoPrincipal ? (
              <Box sx={{pl: 2, pr: 2, pb: 2}}>
                <Paper variant="outlined" sx={{p: 2}}>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Box>
                      <Typography variant="subtitle1">{documentoPrincipal.nombre}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {documentoPrincipal.tipoDocumento} - {documentoPrincipal.version || 'Sin versión'}
                      </Typography>
                      {documentoPrincipal.validoHasta && (
                        <Typography variant="body2" color="text.secondary">
                          Válido hasta: {moment(documentoPrincipal.validoHasta).format('DD/MM/YYYY')}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => verDocumento(documentoPrincipal.id)}>
                      {i18n('common.view')}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            ) : null}

            <Divider sx={{my: 1}} />
          </>
        )} */}

        {/* Documentos Hijos (donde este documento es el principal) */}
        <Divider sx={{my: 1}} />

        <ListItem button onClick={handleClickHijos}>
          <ListItemText primary={i18n('entities.gestorDocumental.fields.documentosHijos')} />
          {openHijos ? <ExpandLess /> : <ExpandMore />}
        </ListItem>

        <Collapse in={openHijos} timeout="auto" unmountOnExit>
          {loadingHijos ? (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{pl: 2, pr: 2, pb: 2}}>
              {documentosHijos && documentosHijos.length > 0 ? (
                documentosHijos.map((doc) => (
                  <Paper key={doc.id} variant="outlined" sx={{p: 2, mb: 1}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <Box>
                        <Typography variant="subtitle1">{doc.nombre}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {doc.tipoDocumento} - {doc.version || 'Sin versión'}
                        </Typography>
                        {doc.validoHasta && (
                          <Typography variant="body2" color="text.secondary">
                            Válido hasta: {moment(doc.validoHasta).format('DD/MM/YYYY')}
                          </Typography>
                        )}
                      </Box>
                      {/* <Button variant="outlined" size="small" endIcon={<ArrowForwardIcon />} onClick={() => verDocumento(doc.id)}>
                        {i18n('common.view')}
                      </Button> */}
                      <DrawerButton
                        drawerId="DrawerGestorDocumentalFormPage"
                        type="icon"
                        tooltipTitle={i18n('common.edit')}
                        buttonTitle={i18n('common.edit')}
                        buttonIcon={<EditIcon />}
                        width={window.innerWidth * 0.55}
                        component={<GestorDocumentalFormPage record={doc} disabled={false} drawer={true} />}
                      />
                    </Box>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" sx={{py: 2}}>
                  {i18n('entities.gestorDocumental.view.noHijos')}
                </Typography>
              )}
            </Box>
          )}
        </Collapse>
      </List>
    </Paper>
  );
};

export default DocumentosRelacionadosView;
